import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiAlertCircle, FiUploadCloud, FiWifiOff } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SectionCard from '../../components/ui/SectionCard';
import LoadingScreen from '../../components/ui/LoadingScreen';
import SurveyQuestionField from '../../components/survey/SurveyQuestionField';
import { getSurveyById } from '../../services/surveyService';
import {
  getSurveyBlocks,
  buildQuestionsByOrder,
  isQuestionVisible,
  validateQuestions,
} from '../../utils/surveyForm';
import {
  buildCapturePayload,
  enqueueCapture,
  syncQueuedCaptures,
  getQueuedCount,
  CaptureQueueFullError,
  MAX_QUEUED_CAPTURES,
} from '../../services/captureService';
import { useAuth } from '../../context/AuthContext';

/**
 * Captura de una encuesta real: arma un formulario dinámico multi-paso
 * a partir de `survey.questions` (una sección por `block`), respetando
 * campos condicionales (`dependsOn`) y validando los obligatorios de
 * cada sección antes de avanzar.
 *
 * Al enviar, la respuesta se guarda primero en una cola local
 * (`captureService`, máx. {@link MAX_QUEUED_CAPTURES} encuestas sin
 * sincronizar) para que el operativo pueda seguir trabajando sin
 * conexión, y se intenta sincronizar en segundo plano contra
 * `POST /captures/sync`.
 */
export default function SurveyCaptureView() {
  const { formId } = useParams();
  const { user } = useAuth();
  const [survey, setSurvey] = useState(undefined); // undefined=cargando, null=no encontrada
  const [loadError, setLoadError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [contact, setContact] = useState({ citizenName: '', address: '' });
  const [answers, setAnswers] = useState({});
  const [stepErrors, setStepErrors] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [submitState, setSubmitState] = useState('idle'); // idle | saving | done
  const [submitError, setSubmitError] = useState(null);
  const [syncState, setSyncState] = useState('idle'); // idle | syncing | synced | error
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let active = true;
    setSurvey(undefined);
    setLoadError(null);
    setOffline(!navigator.onLine);
    setContact({ citizenName: '', address: '' });
    setAnswers({});
    setStepErrors({});
    setStepIndex(0);
    setSubmitState('idle');
    setSubmitError(null);
    setSyncState('idle');

    getSurveyById(formId)
      .then((data) => {
        if (!active) return;
        setSurvey(data);
        if (!navigator.onLine) setOffline(true);
      })
      .catch((err) => {
        if (!active) return;
        setSurvey(null);
        setLoadError(err.message || 'No se pudo cargar la encuesta.');
      });

    return () => {
      active = false;
    };
  }, [formId]);

  const questions = useMemo(() => survey?.questions ?? [], [survey]);
  const blocks = useMemo(() => getSurveyBlocks(questions), [questions]);
  const questionsByOrder = useMemo(() => buildQuestionsByOrder(questions), [questions]);

  const currentBlock = blocks[stepIndex];
  const currentQuestions = useMemo(
    () =>
      questions.filter(
        (q) => q.block === currentBlock && isQuestionVisible(q, answers, questionsByOrder)
      ),
    [questions, currentBlock, answers, questionsByOrder]
  );

  if (survey === undefined) {
    return <LoadingScreen label="Cargando encuesta…" />;
  }

  function handleContactChange(field) {
    return (event) => setContact((prev) => ({ ...prev, [field]: event.target.value }));
  }

  function handleAnswerChange(question, value, validationError) {
    setAnswers((prev) => {
      const prevValue = prev[question.id];
      if (prevValue?.previewUrl && prevValue.previewUrl !== value?.previewUrl) {
        URL.revokeObjectURL(prevValue.previewUrl);
      }
      return { ...prev, [question.id]: value };
    });
    setStepErrors((prev) => {
      const next = { ...prev };
      if (validationError) next[question.id] = validationError;
      else delete next[question.id];
      return next;
    });
  }

  function goToStep(index) {
    setStepIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleNext() {
    const errors = validateQuestions(currentQuestions, answers);
    setStepErrors((prev) => ({ ...prev, ...errors }));
    if (Object.keys(errors).length > 0) return;
    if (stepIndex < blocks.length - 1) goToStep(stepIndex + 1);
  }

  function handleBack() {
    if (stepIndex > 0) goToStep(stepIndex - 1);
  }

  async function handleSubmit() {
    const errors = validateQuestions(currentQuestions, answers);
    setStepErrors((prev) => ({ ...prev, ...errors }));
    if (Object.keys(errors).length > 0) return;

    setSubmitError(null);
    setSubmitState('saving');
    try {
      const capture = await buildCapturePayload({
        survey,
        questionsByOrder,
        answers,
        sectionId: user?.sectionId,
        citizenName: contact.citizenName,
        address: contact.address,
      });
      enqueueCapture(capture);
      setSubmitState('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      attemptBackgroundSync();
    } catch (err) {
      setSubmitState('idle');
      setSubmitError(
        err instanceof CaptureQueueFullError
          ? err.message
          : err.message || 'No se pudo guardar la captura en este dispositivo.'
      );
    }
  }

  async function attemptBackgroundSync() {
    setSyncState('syncing');
    try {
      await syncQueuedCaptures();
      setSyncState('synced');
    } catch {
      setSyncState('error');
    } finally {
      setPendingCount(getQueuedCount());
    }
  }

  if (!survey) {
    return (
      <DashboardLayout title="Encuesta" subtitle="No encontrada">
        {loadError && (
          <div className="form-error" role="alert">
            <FiAlertCircle /> {loadError}
          </div>
        )}
        <SectionCard>
          <p>La encuesta solicitada no existe o fue eliminada.</p>
        </SectionCard>
        <Link to="/data-entry/forms" className="btn btn-outline btn-block">
          <FiArrowLeft /> Volver a formularios
        </Link>
      </DashboardLayout>
    );
  }

  if (submitState === 'done') {
    return (
      <DashboardLayout title="¡Listo!" subtitle={survey.title}>
        <SectionCard>
          <div className="survey-success">
            <FiCheckCircle className="survey-success-icon" />
            <p className="empty-state__title">Respuesta guardada</p>
            <p className="empty-state__description">
              {syncState === 'synced' &&
                'La información ya se sincronizó con el servidor.'}
              {syncState === 'syncing' && 'Sincronizando con el servidor…'}
              {syncState === 'error' &&
                `Se guardó en este dispositivo. Aún no hay conexión para sincronizar (${pendingCount}/${MAX_QUEUED_CAPTURES} pendientes).`}
              {syncState === 'idle' && 'La información quedó guardada en este dispositivo.'}
            </p>
          </div>
        </SectionCard>
        <Link to="/data-entry/forms" className="btn btn-primary btn-block">
          Volver a formularios
        </Link>
      </DashboardLayout>
    );
  }

  const isLastStep = stepIndex === blocks.length - 1;
  const isFirstStep = stepIndex === 0;

  return (
    <DashboardLayout
      title={survey.title}
      subtitle={blocks.length > 1 ? `Sección ${stepIndex + 1} de ${blocks.length}` : 'Captura de datos'}
    >
      {blocks.length > 1 && (
        <div className="progress-track survey-progress">
          <div
            className="progress-fill"
            style={{ width: `${((stepIndex + 1) / blocks.length) * 100}%` }}
          />
        </div>
      )}

      {offline && (
        <div className="form-error form-error--muted" role="status">
          <FiWifiOff /> Sin conexión: mostrando una copia guardada de este formulario.
        </div>
      )}

      {submitError && (
        <div className="form-error" role="alert">
          <FiAlertCircle /> {submitError}
        </div>
      )}

      {isFirstStep && (
        <SectionCard title="Datos de contacto">
          <div className="survey-field">
            <label className="survey-field__label" htmlFor="citizen-name">Nombre del ciudadano</label>
            <input
              id="citizen-name"
              className="survey-input"
              placeholder="Nombre completo"
              value={contact.citizenName}
              onChange={handleContactChange('citizenName')}
            />
          </div>
          <div className="survey-field">
            <label className="survey-field__label" htmlFor="citizen-address">Dirección</label>
            <input
              id="citizen-address"
              className="survey-input"
              placeholder="Calle, número, colonia…"
              value={contact.address}
              onChange={handleContactChange('address')}
            />
          </div>
        </SectionCard>
      )}

      {questions.length === 0 ? (
        <SectionCard>
          <p>Esta encuesta todavía no tiene preguntas configuradas.</p>
        </SectionCard>
      ) : (
        <SectionCard>
          {currentQuestions.map((question) => (
            <SurveyQuestionField
              key={question.id}
              question={question}
              value={answers[question.id]}
              error={stepErrors[question.id]}
              onChange={(value, validationError) =>
                handleAnswerChange(question, value, validationError)
              }
            />
          ))}
        </SectionCard>
      )}

      <div className="survey-nav">
        {stepIndex > 0 && (
          <button type="button" className="btn btn-outline" onClick={handleBack} disabled={submitState === 'saving'}>
            <FiArrowLeft /> Anterior
          </button>
        )}
        {isLastStep ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitState === 'saving'}
          >
            {submitState === 'saving' ? (
              <>
                <FiUploadCloud /> Guardando…
              </>
            ) : (
              <>
                <FiCheckCircle /> Enviar
              </>
            )}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={handleNext}>
            Siguiente <FiArrowRight />
          </button>
        )}
      </div>
    </DashboardLayout>
  );
}



